import React, { useCallback, useContext, useMemo } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Field, Form } from 'react-final-form';
import { Button, Loader } from 'ncoded-component-library';
import DownloadIcon from 'icons/Download.icon';
import SelectField from 'components/SelectField';
import {
  DATA_ROOM_ARCHIVE_FILE_TYPE,
  DataRoomArchiveFileType,
} from 'router/subrouters/DataRoom/subrouters/Settings/pages/SetupPage/components/Snapshots/types';
import {
  ANALYTICS_EXPORT_SECTION,
  ANALYTICS_EXPORT_TIMEFRAME,
  ANALYTICS_REPORT_LAYOUT,
  ANALYTICS_REPORT_PAGE,
  AnalyticsExportSection,
  AnalyticsExportTimeframe,
  AnalyticsReportLayout,
  EXTENDED_ANALYTICS_EXPORT_SECTION,
} from '../../../../../Reports/types';
import RadioGroupField from 'components/RadioGroupField';
import useCreateSelectOptions from 'hooks/useCreateSelectOptions';
import CheckboxFieldGroup from 'components/CheckboxFieldGroup';
import {
  EXPORT_SECTION_TITLE_COPY,
  EXPORT_SECTION_DESCRIPTION_COPY,
  TIMEFRAME_COPY,
} from './data';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import DownloadStatusContext from 'providers/DownloadStatus/DownloadStatus.context';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import downloadService from 'services/downloadService';
import { getArrayLength, getCheckedValuesInObject } from 'utils';

import './DownloadReportForm.styles.scss';

const getIsPdf = (format: DataRoomArchiveFileType) =>
  format === DATA_ROOM_ARCHIVE_FILE_TYPE.PDF;

type FormValues = {
  timeframe: AnalyticsExportTimeframe;
  format: DataRoomArchiveFileType;
  sections: Partial<Record<AnalyticsExportSection, boolean>>;
  reportLayout?: AnalyticsReportLayout;
};

type DownloadReportFormProps = {
  className?: string;
  withExtendedSections: boolean;
  onCancel: () => void;
};

const DownloadReportForm: React.FC<DownloadReportFormProps> = (props) => {
  const { className, withExtendedSections, onCancel } = props;

  const sections = withExtendedSections
    ? EXTENDED_ANALYTICS_EXPORT_SECTION
    : ANALYTICS_EXPORT_SECTION;

  const classes = classNames('download-report-form', className);

  const { t } = useTranslation();

  const {
    dataRoom: { _id },
    preferredAnalyticsTimeframe,
  } = useContext(DataRoomContext);

  const { startAnalyticsExportDownload, failAnalyticsExportDownload } =
    useContext(DownloadStatusContext);

  const fileTypeOptions = useCreateSelectOptions()(
    Object.values(DATA_ROOM_ARCHIVE_FILE_TYPE),
  );

  const reportLayoutOptions = useCreateSelectOptions()(
    Object.values(ANALYTICS_REPORT_LAYOUT),
  );

  const exportSectionOptions = useMemo(
    () =>
      Object.values(sections).map((value) => ({
        value,
        label: (
          <span className="download-report-form__section-option">
            <span className="download-report-form__section-title">
              {t(EXPORT_SECTION_TITLE_COPY[value])}
            </span>
            <span className="download-report-form__section-description">
              {t(EXPORT_SECTION_DESCRIPTION_COPY[value])}
            </span>
          </span>
        ),
      })),
    [sections, t],
  );

  const initialValues: Partial<FormValues> = useMemo(
    () => ({
      timeframe:
        preferredAnalyticsTimeframe || ANALYTICS_EXPORT_TIMEFRAME.SEVEN_DAYS,
      format: DATA_ROOM_ARCHIVE_FILE_TYPE.PDF,
      reportLayout: ANALYTICS_REPORT_LAYOUT.CONDESENSED_SUMMARY,
      sections: Object.values(sections).reduce((acc, section) => {
        acc[section] = true;
        return acc;
      }, {} as Record<AnalyticsExportSection, boolean>),
    }),
    [preferredAnalyticsTimeframe, sections],
  );

  const onSubmit = useCallback(
    async ({ timeframe, format, sections, reportLayout }: FormValues) => {
      const selectedSections = getCheckedValuesInObject(sections);

      try {
        startAnalyticsExportDownload(
          t('exportReportSummary', {
            count: getArrayLength(selectedSections),
            timeframe: t(TIMEFRAME_COPY[timeframe]),
            format: t(format),
          }),
          getIsPdf(format),
        );

        await dataRoomApi.reports.exportAnalytics(_id, {
          format,
          timeframe,
          sections: selectedSections,
          uniqueId: downloadService.downloadUniqueId,
          reportLayout: getIsPdf(format) ? reportLayout : undefined,
          page: withExtendedSections
            ? ANALYTICS_REPORT_PAGE.DASHBOARD
            : ANALYTICS_REPORT_PAGE.ANALYTICS,
        });

        onCancel();
      } catch {
        failAnalyticsExportDownload();
      }
    },
    [
      _id,
      failAnalyticsExportDownload,
      onCancel,
      startAnalyticsExportDownload,
      t,
      withExtendedSections,
    ],
  );

  return (
    <Form
      initialValues={initialValues}
      onSubmit={onSubmit}
      render={({ submitting, values: { format }, handleSubmit }) => (
        <form onSubmit={handleSubmit} className={classes}>
          {submitting && <Loader />}

          <Field
            name="timeframe"
            required
            label={t('period')}
            component={SelectField}
            options={Object.values(ANALYTICS_EXPORT_TIMEFRAME).map((value) => ({
              value,
              label: t(TIMEFRAME_COPY[value]),
            }))}
          />

          <div className="radio-buttons-fields">
            <RadioGroupField
              label={t('chooseFileType')}
              required
              name="format"
              options={fileTypeOptions}
            />

            {getIsPdf(format) && (
              <RadioGroupField
                label={t('reportLayout')}
                required
                name="reportLayout"
                options={reportLayoutOptions}
              />
            )}
          </div>

          <CheckboxFieldGroup
            name="sections"
            required
            label={t('informationToExport')}
            options={exportSectionOptions}
          />

          <div className="actions">
            <Button variant="link" onClick={onCancel}>
              {t('cancel')}
            </Button>

            <Button type="submit" icon={DownloadIcon} disabled={submitting}>
              {t('export')}
            </Button>
          </div>
        </form>
      )}
    />
  );
};

export default DownloadReportForm;
